import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styles from './VehicleRide.module.css';
import { useAppContext } from '@/context/AppContext';
import LocationSourceSelector from '@/components/LocationSourceSelector/LocationSourceSelector';
import dayjs from 'dayjs';
import { formatTimeAMPM } from '@/utils/helpers';

interface LocationSource {
  id: string;
  name: string;
}

interface VehicleRideDetails {
  bus_number: string;
  vehicle_id: number;
  license_plate: string;
  driver_token: string;
  ride_date: string;
  start_time: string;
  end_time: string;
  active_sources?: LocationSource[];
}

const VehicleRidePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getSourceBackendUrl, apiKey } = useAppContext();
  const [vehicleRide, setVehicleRide] = useState<VehicleRideDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [fetchingLocationData, setFetchingLocationData] = useState<boolean>(false);
  
  // Format date for display using dayjs (DD-MM-YYYY → MMMM D, YYYY)
  const formatDate = (dateString: string): string => {
    try {
      // Check if date is in DD-MM-YYYY format
      if (dateString.match(/^\d{2}-\d{2}-\d{4}$/)) {
        const [day, month, year] = dateString.split('-').map(Number);
        return dayjs(`${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`)
          .format('MMMM D, YYYY');
      }
      // Fallback to original format
      return dayjs(dateString).format('MMMM D, YYYY');
    } catch (e) {
      return dateString || 'N/A';
    }
  };
  
  // Calculate duration between two time strings
  const calculateDuration = (startTime: string, endTime: string): number => {
    try {
      // Use a fixed date (2000-01-01) to ensure we're only comparing times
      const start = startTime.match(/^\d{2}:\d{2}(:\d{2})?$/) 
        ? dayjs(`2000-01-01 ${startTime}`) 
        : dayjs(startTime);
      
      let end = endTime.match(/^\d{2}:\d{2}(:\d{2})?$/) 
        ? dayjs(`2000-01-01 ${endTime}`) 
        : dayjs(endTime);
      
      // Calculate difference in minutes
      let diffMinutes = end.diff(start, 'minute');
      
      // If end time is before start time, assume it's the next day
      if (diffMinutes < 0) {
        end = end.add(24, 'hour');
        diffMinutes = end.diff(start, 'minute');
      }
      
      return diffMinutes;
    } catch (e) {
      console.error('Error calculating duration:', e);
      return 0;
    }
  };
  
  // Format duration in minutes to a human-readable string
  const formatDuration = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    return remainingMinutes === 0
      ? `${hours} hour${hours !== 1 ? 's' : ''}`
      : `${hours} hour${hours !== 1 ? 's' : ''} ${remainingMinutes} min`;
  };
  
  // Start the simulation by fetching location data and navigating to the simulation page
  const startSimulation = async () => {
    if (!id || selectedSources.length === 0) return;

    // Store simulation data in localStorage for the simulation page
    if (vehicleRide) {
      // Store vehicle info
      localStorage.setItem('simulation_vehicle_info', JSON.stringify(vehicleRide));
      // Store vehicle ID for API calls
      localStorage.setItem('simulation_vehicle_id', vehicleRide.vehicle_id.toString() || '');
      // Store original vehicle_ride_id for navigation
      localStorage.setItem('simulation_vehicle_ride_id', id || '');
      // Store selected sources
      localStorage.setItem('simulation_sources', selectedSources.join(','));
    }

      // Navigate to the simulation page
      navigate('/simulation');
  };

  useEffect(() => {
    // Use a ref to track if the component is mounted
    let isMounted = true;
    
    const fetchVehicleRideDetails = async () => {
      if (!id || !apiKey) {
        setError('Missing vehicle ride ID or API key');
        setLoading(false);
        return;
      }

      try {
        const backendUrl = getSourceBackendUrl();
        
        // First validate the connection
        console.log('Validating connection before fetching vehicle ride details');
        const validationUrl = `${backendUrl}/api/tools/tracking-sim/validate-connection/`;
        
        const validationResponse = await fetch(validationUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-tracking-sim-api-key': apiKey
          },
          mode: 'cors'
        });
        
        // Check if component is still mounted before proceeding
        if (!isMounted) return;
        
        if (!validationResponse.ok) {
          const errorData = await validationResponse.json();
          throw new Error(errorData.error || 'Connection validation failed');
        }
        
        // After successful validation, fetch vehicle ride details
        console.log('Connection validated, fetching vehicle ride details');
        const url = `${backendUrl}/api/tools/tracking-sim/fetch-vehicle-ride-details/?vehicle_ride_id=${id}`;
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-tracking-sim-api-key': apiKey
          },
          mode: 'cors'
        });
        
        // Check if component is still mounted before proceeding
        if (!isMounted) return;
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to fetch vehicle ride details');
        }
        
        const data = await response.json();
        if (isMounted) {
          setVehicleRide(data);
          
          // Store driver_token in localStorage for future use
          if (data.driver_token) {
            localStorage.setItem('driverToken', data.driver_token);
            console.log('Driver token stored for future use');
          }
        }
      } catch (err: any) {
        console.error('Error in vehicle ride workflow:', err);
        if (isMounted) {
          setError(err.message || 'Failed to process vehicle ride request');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchVehicleRideDetails();
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [id, apiKey, getSourceBackendUrl]);

  const handleBack = () => {
    navigate('/');
  };

  return (
    <>
      <Helmet>
        <title>{vehicleRide ? `Ride ${id}` : 'Vehicle Ride'} | Tracking Simulator v2</title>
      </Helmet>

      <div className={styles.container}>
        <button className={styles.backButton} onClick={handleBack}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.backIcon}>
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          <span>Back to Home</span>
        </button>

        <div className={styles.content}>
          {loading ? (
            <div className={styles.loadingContainer}>
              <div className={styles.loadingSpinner}></div>
              <p>Loading vehicle ride details...</p>
            </div>
          ) : error ? (
            <div className={styles.errorContainer}>
              <h2 className={styles.errorTitle}>Error</h2>
              <p className={styles.errorMessage}>{error}</p>
              <button className={styles.retryButton} onClick={() => window.location.reload()}>
                Retry
              </button>
            </div>
          ) : vehicleRide ? (
            <>
              <div className={styles.pageHeader}>
                <h1 className={styles.title}>Vehicle Ride Details</h1>
                <div className={styles.rideMetaInfo}>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Bus</span>
                    <span className={styles.metaValue}>{vehicleRide.bus_number}</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Vehicle ID</span>
                    <span className={styles.metaValue}>{vehicleRide.vehicle_id}</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Date</span>
                    <span className={styles.metaValue}>{formatDate(vehicleRide.ride_date)}</span>
                  </div>
                </div>
              </div>
              
              <div className={styles.twoColumnLayout}>
                <div className={styles.leftColumn}>
                  <div className={styles.card}>
                    <h2 className={styles.cardTitle}>Ride Information</h2>
                    <div className={styles.compactInfoGrid}>
                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>Start Time</span>
                        <span className={styles.infoValue}>{formatTimeAMPM(vehicleRide.start_time)}</span>
                      </div>
                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>End Time</span>
                        <span className={styles.infoValue}>{formatTimeAMPM(vehicleRide.end_time)}</span>
                      </div>
                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>Duration</span>
                        <span className={styles.infoValue}>
                          {(() => {
                            try {
                              const diffMinutes = calculateDuration(vehicleRide.start_time, vehicleRide.end_time);
                              return formatDuration(diffMinutes);
                            } catch (e) {
                              console.error('Error calculating duration:', e);
                              return 'N/A';
                            }
                          })()}
                        </span>
                      </div>
                    </div>
                    
                    <LocationSourceSelector 
                      activeSources={vehicleRide.active_sources || []}
                      onSourcesSelected={(sources) => setSelectedSources(sources)}
                    />
                  </div>
                </div>
                
                <div className={styles.rightColumn}>
                  <div className={styles.card}>
                    <h2 className={styles.cardTitle}>Tracking Simulator</h2>
                    <div className={styles.simulatorContent}>
                      {selectedSources.length > 0 ? (
                        <div className={styles.simulatorInfo}>
                          <p>Simulation will run using:</p>
                          <ul className={styles.sourcesList}>
                            {selectedSources.map(sourceId => {
                              const sourceName = vehicleRide.active_sources?.find(s => s.id === sourceId)?.name || 
                                (sourceId === '1' ? 'Cityflo' : 
                                 sourceId === '2' ? 'Fleetx' : 
                                 sourceId === '3' ? 'Intangles' : 
                                 sourceId === '4' ? 'Cautio' : 'Unknown');
                              return (
                                <li key={sourceId} className={styles.sourceItem}>
                                  <span className={`${styles.sourceIndicator} ${styles[`source${sourceId}`]}`}></span>
                                  {sourceName}
                                </li>
                              );
                            })}
                          </ul>
                          <button 
                            className={styles.startButton} 
                            onClick={() => startSimulation()}
                            disabled={fetchingLocationData}
                          >
                            {fetchingLocationData ? 'Loading...' : 'Start Simulation'}
                          </button>
                        </div>
                      ) : (
                        <p>Please select at least one location source to continue.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className={styles.notFoundContainer}>
              <h2 className={styles.notFoundTitle}>Vehicle Ride Not Found</h2>
              <p className={styles.notFoundMessage}>
                The vehicle ride with ID {id} could not be found.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default VehicleRidePage;
